import type { CalEvent, WorkingHours } from "../types";
import { DAY_END_MIN, isActive } from "./day";
import { addDays, atDayMinutes, dayKeyOf, dayStart, minutesOfDay } from "./time";

/*
 * Finding free time: the mockup's nextFree(), on real data. Your own busy time
 * comes from your calendar; colleagues' from getSchedule. Anyone whose
 * availability couldn't be read is "unknown" and is never counted as free.
 */

export interface Busy {
  readonly startMs: number;
  readonly endMs: number;
}

export interface Schedule {
  readonly status: "ok" | "unknown";
  readonly busy: readonly Busy[];
}

/** Candidate starts are on this step. */
const STEP_MIN = 15;
/** Today's first candidate is at least this far from now. */
const LEAD_MIN = 20;
/** How many working days ahead to look. */
export const SEARCH_WORKDAYS = 5;
/** Stop after this many calendar days even if few are working days. */
const MAX_DAYS_SCANNED = 14;

const overlaps = (busy: readonly Busy[], startMs: number, endMs: number) =>
  busy.some((b) => b.startMs < endMs && startMs < b.endMs);

/**
 * Whether a slot built from wall-clock minutes is real. On the day clocks go
 * forward an hour of local time doesn't exist, so a start there lands on the
 * same instant as the hour after it; on the day they go back an hour repeats,
 * so a slot across it lasts an hour longer than asked.
 */
const isRealSlot = (start: Date, end: Date, startMin: number, durationMin: number) =>
  minutesOfDay(start) === startMin && end.getTime() - start.getTime() === durationMin * 60000;

/** Your busy time, leaving out `excludeId` (the meeting being moved). */
export function ownBusy(events: readonly CalEvent[], excludeId: string | null): Busy[] {
  return events
    .filter((e) => e.id !== excludeId && !e.isAllDay && isActive(e))
    .map((e) => ({ startMs: e.start.getTime(), endMs: e.end.getTime() }));
}

export interface FreeSlot {
  readonly start: Date;
  readonly end: Date;
  /** People whose availability couldn't be read, so they weren't checked. */
  readonly unknown: readonly string[];
}

export interface NextFreeParams {
  readonly events: readonly CalEvent[];
  /** The meeting being moved: left out of your busy time, and its current slot ignored in attendees' calendars. */
  readonly moving: CalEvent | null;
  readonly people: readonly string[];
  readonly schedules: ReadonlyMap<string, Schedule>;
  readonly durationMin: number;
  readonly fromDay: string;
  /** Earliest start on `fromDay`, in minutes of the day. */
  readonly fromMin: number;
  readonly hours: WorkingHours;
  readonly now: Date;
  /** Search only `fromDay` (Focus Hour), or the next few working days (moving a meeting). */
  readonly sameDayOnly?: boolean;
  /**
   * On `fromDay`, take any free time from `fromMin` to midnight, not only
   * working hours, so a late clash stays on its own day when it can.
   */
  readonly firstDayAnyTime?: boolean;
}

/**
 * A colleague's busy times. Their calendar still shows `moving` at its current
 * time, so that slot doesn't count against them.
 */
function attendeeBusy(schedules: ReadonlyMap<string, Schedule>, moving: CalEvent | null) {
  const startMs = moving?.start.getTime();
  const endMs = moving?.end.getTime();
  return (email: string): readonly Busy[] =>
    (schedules.get(email)?.busy ?? []).filter((b) => !(b.startMs === startMs && b.endMs === endMs));
}

/**
 * Whether nextFree looks at `day`: never a day that has gone, and otherwise
 * only working days, unless it searches one day only or any time on the first.
 */
function searchesDay(p: NextFreeParams, day: string, todayKey: string, anyTime: boolean): boolean {
  if (day < todayKey) return false;
  return p.sameDayOnly === true || anyTime || p.hours.days.has(dayStart(day).getDay());
}

/** The minutes searched on `day`: from the earliest allowed start, on the step, to the end of the window. */
function searchWindow(p: NextFreeParams, day: string, todayKey: string, anyTime: boolean): { from: number; until: number } {
  let from = anyTime ? p.fromMin : p.hours.startMin;
  if (day === p.fromDay) from = Math.max(from, p.fromMin);
  if (day === todayKey) from = Math.max(from, minutesOfDay(p.now) + LEAD_MIN);
  return { from: Math.ceil(from / STEP_MIN) * STEP_MIN, until: anyTime ? DAY_END_MIN : p.hours.endMin };
}

/** The first real slot of `durationMin` in `window` on `day` that `isFree` accepts. */
function firstSlotOnDay(
  day: string,
  window: { from: number; until: number },
  durationMin: number,
  isFree: (startMs: number, endMs: number) => boolean,
): { start: Date; end: Date } | null {
  for (let s = window.from; s + durationMin <= window.until; s += STEP_MIN) {
    const start = atDayMinutes(day, s);
    const end = atDayMinutes(day, s + durationMin);
    if (isRealSlot(start, end, s, durationMin) && isFree(start.getTime(), end.getTime())) return { start, end };
  }
  return null;
}

/** The first slot within working hours where you, and everyone whose availability is known, are free. */
export function nextFree(p: NextFreeParams): FreeSlot | null {
  const mine = ownBusy(p.events, p.moving?.id ?? null);
  const theirs = attendeeBusy(p.schedules, p.moving);
  const unknown = p.people.filter((e) => p.schedules.get(e)?.status !== "ok");
  const known = p.people.filter((e) => p.schedules.get(e)?.status === "ok");
  const isFree = (a: number, b: number) => !overlaps(mine, a, b) && !known.some((email) => overlaps(theirs(email), a, b));

  const todayKey = dayKeyOf(p.now);
  const daysWanted = p.sameDayOnly ? 1 : SEARCH_WORKDAYS;
  let checked = 0;
  for (let i = 0; i < MAX_DAYS_SCANNED && checked < daysWanted; i++) {
    const day = addDays(p.fromDay, i);
    const anyTime = p.firstDayAnyTime === true && day === p.fromDay;
    if (!searchesDay(p, day, todayKey, anyTime)) continue;
    checked++;
    const slot = firstSlotOnDay(day, searchWindow(p, day, todayKey, anyTime), p.durationMin, isFree);
    if (slot) return { ...slot, unknown };
  }
  return null;
}

/**
 * Whether `email` has something else on during `event` (a meeting, or just a
 * time), or null when their calendar couldn't be read. For someone already
 * invited, their own copy of this meeting (the busy time that matches it
 * exactly) doesn't count.
 */
export function busyDuring(
  schedules: ReadonlyMap<string, Schedule>,
  email: string,
  event: Pick<CalEvent, "start" | "end">,
  invited: boolean,
): boolean | null {
  const schedule = schedules.get(email);
  if (schedule?.status !== "ok") return null;
  const a = event.start.getTime();
  const b = event.end.getTime();
  return schedule.busy.some((x) => x.startMs < b && a < x.endMs && !(invited && x.startMs === a && x.endMs === b));
}

/** The time finder offers starts on this step, as the mockup does. */
const FINDER_STEP_MIN = 30;

/** A time the finder offers. You are always free then. */
export interface Candidate {
  readonly start: Date;
  readonly end: Date;
  /** Colleagues whose calendars show them busy then. */
  readonly busy: readonly string[];
}

export interface FindSlotsParams {
  /** Your events across the whole search range, not just the week on screen. */
  readonly events: readonly CalEvent[];
  /** The meeting being moved: its current time is not offered. */
  readonly moving: CalEvent | null;
  readonly people: readonly string[];
  readonly schedules: ReadonlyMap<string, Schedule>;
  readonly durationMin: number;
  readonly hours: WorkingHours;
  readonly now: Date;
}

/**
 * Every time in working hours over the next few working days when you are
 * free, with who else is busy then; the mockup's candidates(). Anyone whose
 * availability couldn't be read is listed in `unknown` and never counted as free.
 */
export function findSlots(p: FindSlotsParams): { slots: Candidate[]; unknown: string[] } {
  const mine = ownBusy(p.events, p.moving?.id ?? null);
  const theirs = attendeeBusy(p.schedules, p.moving);
  const unknown = p.people.filter((e) => p.schedules.get(e)?.status !== "ok");
  const known = p.people.filter((e) => p.schedules.get(e)?.status === "ok");
  const currentStart = p.moving?.start.getTime();

  const todayKey = dayKeyOf(p.now);
  const slots: Candidate[] = [];
  let checked = 0;
  for (let i = 0; i < MAX_DAYS_SCANNED && checked < SEARCH_WORKDAYS; i++) {
    const day = addDays(todayKey, i);
    if (!p.hours.days.has(dayStart(day).getDay())) continue;
    checked++;
    let from = p.hours.startMin;
    if (day === todayKey) from = Math.max(from, minutesOfDay(p.now) + LEAD_MIN);
    from = Math.ceil(from / FINDER_STEP_MIN) * FINDER_STEP_MIN;
    for (let s = from; s + p.durationMin <= p.hours.endMin; s += FINDER_STEP_MIN) {
      const start = atDayMinutes(day, s);
      const end = atDayMinutes(day, s + p.durationMin);
      if (!isRealSlot(start, end, s, p.durationMin)) continue;
      const a = start.getTime();
      const b = end.getTime();
      if (a === currentStart || overlaps(mine, a, b)) continue;
      slots.push({ start, end, busy: known.filter((email) => overlaps(theirs(email), a, b)) });
    }
  }
  return { slots, unknown };
}

/** The span the finder can offer from, for reading calendars once up front. */
export function finderRange(today: string): { start: Date; end: Date } {
  return { start: dayStart(today), end: dayStart(addDays(today, MAX_DAYS_SCANNED)) };
}
